import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Upload, FileText, X, Loader2, CheckCircle2 } from "lucide-react";
import {
  parseInvoiceFile,
  type PARInvoiceRow,
  type VendorSummary,
} from "@/lib/par/csvInvoiceParser";

interface InvoiceUploadZoneProps {
  onParsed: (rows: PARInvoiceRow[], vendors: VendorSummary[], fileName: string) => void;
}

export function InvoiceUploadZone({ onParsed }: InvoiceUploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState<number>(0);
  const [parsing, setParsing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const processFile = async (file: File) => {
    const validTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    const validExt = [".csv", ".xlsx", ".xls"];
    const hasValidType = validTypes.some((t) => file.type.includes(t));
    const hasValidExt = validExt.some((e) => file.name.toLowerCase().endsWith(e));

    if (!hasValidType && !hasValidExt) {
      toast({
        title: "Invalid file type",
        description: "Please upload a CSV or Excel file.",
        variant: "destructive",
      });
      return;
    }

    setParsing(true);
    setFileName(file.name);
    try {
      const { rows, vendors } = await parseInvoiceFile(file);
      setRowCount(rows.length);
      onParsed(rows, vendors, file.name);
      toast({
        title: "Invoice data loaded",
        description: `Parsed ${rows.length} invoice rows across ${vendors.length} vendors.`,
      });
    } catch (err) {
      console.error("Parse error:", err);
      toast({
        title: "Parse failed",
        description: err instanceof Error ? err.message : "Could not parse the file.",
        variant: "destructive",
      });
      setFileName(null);
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  if (fileName && rowCount > 0 && !parsing) {
    return (
      <div className="flex items-center gap-2 text-sm min-w-0">
        <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
        <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <span className="font-mono text-xs truncate min-w-0">{fileName}</span>
        <Badge
          variant="outline"
          className="text-[10px] px-1.5 py-0 text-success border-success/30 bg-success/5 flex-shrink-0"
        >
          {rowCount.toLocaleString()} rows
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 ml-1 flex-shrink-0"
          onClick={() => {
            setFileName(null);
            setRowCount(0);
            onParsed([], [], "");
          }}
        >
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <div
        className={`rounded-lg border-2 border-dashed transition-all cursor-pointer ${
          isDragging
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary/50 hover:bg-muted/30"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => !parsing && inputRef.current?.click()}
      >
        <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
          {parsing ? (
            <>
              <Loader2 className="w-5 h-5 text-primary animate-spin mb-2" />
              <p className="text-xs text-muted-foreground">Parsing {fileName}…</p>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 text-muted-foreground mb-2" />
              <p className="text-sm font-medium text-foreground">Drop invoice CSV/XLSX here</p>
              <p className="text-xs text-muted-foreground mt-1">
                or <span className="text-primary underline">browse</span> — AP invoice details
                export
              </p>
            </>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) processFile(file);
          e.target.value = "";
        }}
      />
    </>
  );
}
